import {
  GoogleAuthProvider,
  User as FirebaseUser,
  signInWithPopup,
  signInWithRedirect,
  OAuthProvider,
} from 'firebase/auth';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { FirebaseContextValue } from './firebase.types';
import { firebaseAuth } from './firebase.setup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  endSignIn,
  initAuth,
  startSignIn,
  throwSignInError,
  updateIsLoggedIn,
} from '../../store/auth/auth.slice';
import {
  getIdentity,
  signOut as signOutRequest,
} from '../../store/auth/auth.thunks';
import {
  ChromeMessage,
  ChromeMessageType,
  OutlookAddInMessage,
  OutlookAddInMessageType,
} from '../../types';
import {
  EXTENSION_ID,
  MINIMUM_EXTENSION_VERSION,
  OUTLOOK_ADDIN,
} from '../../constants/config';
import { ROUTES } from '../../routes';

const FirebaseContext = createContext<FirebaseContextValue>({
  user: null,
  isLoggedIn: false,
  isInitialized: false,
  isSignInLoading: false,
  isSignInError: false,
  googleSignIn: () => {},
  microsoftSignIn: () => {},
  signOut: () => {},
});

export const FirebaseProvider: React.FC = ({ children }) => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const isLoggedInValue = useAppSelector((state) => state.auth.isLoggedIn);
  const isInitialized = useAppSelector((state) => state.auth.isInitialized);
  const isSignInError = useAppSelector((state) => state.auth.isError.signIn);

  const isSignInLoading = useAppSelector(
    (state) => state.auth.isLoading.signIn
  );

  const isIdentityError = useAppSelector(
    (state) => state.auth.isError.identity
  );

  const extensionAuthCode = useAppSelector(
    (state) => state.auth.extensionAuthCode
  );

  const { pathname } = useLocation();

  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);

  const isLoggedIn =
    isLoggedInValue && !isSignInLoading && !isSignInError && !isIdentityError;

  const isLogoutPage = pathname === ROUTES.LOGOUT;

  const runSignInFlow = useCallback(
    async (callback: () => Promise<unknown> | void) => {
      try {
        dispatch(startSignIn());
        await callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        dispatch(throwSignInError());
        dispatch(updateIsLoggedIn(false));
        firebaseAuth.signOut();
      } finally {
        dispatch(endSignIn());
      }
    },
    [dispatch]
  );

  const signOut = useCallback(async () => {
    try {
      await dispatch(signOutRequest()).unwrap();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      dispatch(updateIsLoggedIn(false));
      firebaseAuth.signOut();
    }
  }, [dispatch]);

  const googleSignIn = useCallback(
    (email?: string) => {
      runSignInFlow(() => {
        const provider = new GoogleAuthProvider();

        provider.setCustomParameters({
          access_type: 'offline',
          include_granted_scopes: 'true',
          ...(email ? { login_hint: email } : {}),
        });

        // provider.addScope('email');
        // provider.addScope('https://www.googleapis.com/auth/gmail.readonly');
        // provider.addScope('https://www.googleapis.com/auth/gmail.send');
        // provider.addScope('https://www.googleapis.com/auth/contacts');
        // provider.addScope('https://www.googleapis.com/auth/calendar');

        return signInWithPopup(firebaseAuth, provider);
      });
    },
    [runSignInFlow]
  );
  const microsoftSignIn = useCallback(() => {
    runSignInFlow(() => {
      const provider = new OAuthProvider('microsoft.com');

      provider.addScope('openid');
      provider.addScope('profile');
      provider.addScope('email');
      provider.addScope('offline_access');
      provider.addScope('User.Read');
      provider.addScope('Mail.Read');
      provider.addScope('Mail.Send');
      provider.addScope('Calendars.Read');
      provider.addScope('Contacts.ReadWrite');

      provider.setCustomParameters({ prompt: 'select_account' });

      if (window.name === OUTLOOK_ADDIN.FLOW_NAME) {
        return signInWithRedirect(firebaseAuth, provider);
      }

      return signInWithPopup(firebaseAuth, provider);
    });
  }, [runSignInFlow]);

  // const microsoftSignIn = useCallback(
  //   (email?: string) => {
  //     runSignInFlow(() => {
  //       const provider = new OAuthProvider('microsoft.com');
  //       provider.addScope('User.Read');
  //       provider.addScope('offline_access');
  //       provider.addScope('Calendars.Read');
  //       // provider.setCustomParameters({
  //       //   redirect_uri: 'https://staging.account.substrata.me/',
  //       // });
  //       provider.setCustomParameters({
  //         redirect_uri: 'http://localhost:3002/',
  //       });
  //       provider.setCustomParameters({
  //         prompt: 'select_account',
  //         ...(email ? { login_hint: email } : {}),
  //       });

  //       return signInWithPopup(firebaseAuth, provider);
  //     });
  //   },
  //   [runSignInFlow]
  // );

  const firebaseContextValue = useMemo(
    () => ({
      user: firebaseUser,
      isLoggedIn,
      isInitialized,
      isSignInLoading,
      isSignInError,
      googleSignIn,
      microsoftSignIn,
      signOut,
    }),
    [
      firebaseUser,
      isLoggedIn,
      isInitialized,
      isSignInLoading,
      isSignInError,
      googleSignIn,
      microsoftSignIn,
      signOut,
    ]
  );

  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged(async (currentUser) => {
      setFirebaseUser(currentUser);
      dispatch(initAuth(!!currentUser));
    });

    return unsubscribe;
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      if (!isLogoutPage && !user && isLoggedIn) {
        try {
          await dispatch(getIdentity()).unwrap();
        } catch (error) {
          console.log(error);

          dispatch(updateIsLoggedIn(false));
          firebaseAuth.signOut();
        }
      }
    })();
  }, [dispatch, isLoggedIn, isLogoutPage, user]);

  useEffect(() => {
    if (
      extensionAuthCode &&
      window.name !== OUTLOOK_ADDIN.FLOW_NAME &&
      window.chrome?.runtime
    ) {
      const message: ChromeMessage<ChromeMessageType.LOGIN> = {
        type: ChromeMessageType.LOGIN,
        payload: {
          authCode: extensionAuthCode,
          minimumExtensionVersion: MINIMUM_EXTENSION_VERSION,
        },
      };

      window.chrome.runtime.sendMessage(EXTENSION_ID, message, (response) => {
        if (response) {
          // eslint-disable-next-line no-console
          console.log(response);
        } else {
          // eslint-disable-next-line no-console
          console.error(window.chrome.runtime.lastError);
        }
      });
    }

    if (extensionAuthCode && window.name === OUTLOOK_ADDIN.FLOW_NAME) {
      const message: OutlookAddInMessage<OutlookAddInMessageType.LOGIN> = {
        type: OutlookAddInMessageType.LOGIN,
        payload: {
          authCode: extensionAuthCode,
        },
      };

      if (window.Office?.context?.ui) {
        try {
          window.Office.context?.ui?.messageParent?.(JSON.stringify(message), {
            targetOrigin: OUTLOOK_ADDIN.ORIGIN,
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error sending message to parent:', error);
        }
      } else {
        try {
          window.opener?.postMessage(
            JSON.stringify(message),
            OUTLOOK_ADDIN.ORIGIN
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error sending message to parent:', error);
        }
      }
    }
  }, [extensionAuthCode]);

  return (
    <FirebaseContext.Provider value={firebaseContextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextValue =>
  useContext(FirebaseContext);
